<template>
    <b-container fluid class="p-2 objects-container" style="height: 100%;">
        <grid
            ref="reservations"
            :fetchCallback="fetchReservations"
            :columnDefs="columnDefs"
            :isSearch="true"
            :suppressScrollOnNewData="true"
            :maxBlocksInCache="null"
            :master-detail="true"
            :detailCellRendererParams="detailCellRendererParams"
            :isRowMaster="isRowMaster"
            @on-grid-ready="onGridReady"
            defaultSort="modified"
            :is-auto-width="false"
            :statusBar="statusBar"
        >
            <form-square-switch
                class="mr-1 show-disabled-toggle"
                v-model="autoRefresh"
                :labels="{ checked: 'Auto Refresh', unchecked: 'Auto Refresh' }"
                :isToolbar="true"
            />
            <form-square-switch
                class="mr-1 show-disabled-toggle"
                v-model="showErrorsOnly"
                :labels="{ checked: 'Errors Only', unchecked: 'Errors Only' }"
                :isToolbar="true"
            />            
    
        </grid>
    </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import Grid from '../generic/grid/grid.vue';
import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
import LastErrorCellRenderer from './last-error-cell-renderer.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import SummaryBar from './summary-bar.vue';
import API from '../../api';
import { QUICKBOOKS_OBJECTS_ENDPOINT } from '../../endpoints';

//<form-square-switch
//    class="mr-1 show-disabled-toggle"
//    v-model="showDeleted"
//    :labels="{ checked: 'Show Deleted', unchecked: 'Show Deleted' }"
//    :isToolbar="true"
///>

export default {
    name: 'QuickbooksObjectsSimplified',
    components: {
        FormSquareSwitch,
        Grid,
        lastErrorCellRenderer: LastErrorCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
        summaryBar: SummaryBar,
    },
    data() {
        return {
            lastResyncedItemId: null,
            statusBar: null,
        };
    },
    computed: {
        ...mapGetters('quickbooks', ['quickbooksData', 'shouldRefreshReservations']),
        quickBooksBaseURL() {
            return this.quickbooksData?.quickbooks_base_url;
        },
        autoRefresh: {
            get() {
                return this.$store.getters['quickbooks/autoRefreshReservations'];
            },
            set(value) {
                this.$store.commit('quickbooks/setAutoRefreshReservations', value);
            },
        },
        showErrorsOnly: {
            get() {
                return this.$store.getters['quickbooks/showErrorsOnly'];
            },
            set(value) {
                this.$store.commit('quickbooks/setShowErrorsOnly', value);
            },
        },
        commonColumnDefs() {
            return [
                {
                    field: 'quickbooks_id',
                    headerName: 'QB ID',
                    minWidth: 120,
                    maxWidth: 120,
                    cellRenderer: ({ data }) => {
                        const suffix = this.getObjectSufix(data);
                        return data.deleted
                            ? data.quickbooks_id
                            : data.quickbooks_id
                                ? `<a target='_blank' href='${this.quickBooksBaseURL}/${suffix}'>${
                                      data.quickbooks_id
                                  }</a>`
                                : '-';
                    },
                },
                {
                    field: 'last_error',
                    headerName: 'Last Sync Error',
                    cellRenderer: 'lastErrorCellRenderer',
                    sortable: false,
                    autoWidthExclude: true,
                },
                {
                    field: 'deleted',
                    headerName: 'Deleted?',
                    type: 'yesNoColumnNew',
                    minWidth: 110,
                    maxWidth: 110,
                    valueGetter: ({ data, node }) => (node.rowPinned ? null : data.deleted),
                    cellClass: 'd-flex justify-content-center',
                    headerClass: 'ag-centered-header',
                    cellRendererParams: { plain: true, invisible: true },
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    type: 'yesNoColumnNew',
                    minWidth: 100,
                    maxWidth: 100,
                    valueGetter: ({ data, node }) => {
                        if (node.rowPinned) {
                            return null;
                        }

                        if (node.master) {
                            const payments = data.payments || [];
                            const hasInvalidPayment = payments.some((payment) => payment.last_error !== null);
                            if (hasInvalidPayment) {
                                return false;
                            }
                        }

                        return data.last_error === null;
                    },
                    cellClass: 'd-flex justify-content-center',
                    headerClass: 'ag-centered-header',
                },
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'actionsCellRenderer',
                    maxWidth: 100,
                    minWidth: 100,
                    sortable: false,
                    cellClass: 'd-flex justify-content-center',
                    headerClass: 'ag-centered-header',
                    cellRendererParams: ({ data, node }) => {
                        return {
                            setBusy: (value) => {
                                this.$parent.$emit('busy', value);
                            },
                            refresh: async (idToExpand, objType) => {
                                await this.refreshReservations();
                                if (idToExpand && objType === 'PAYMENT') {
                                    setTimeout(() => {
                                        let rowsToExpand = [];
                                        this.$refs.reservations.gridOptions.api.forEachNode((node) => {
                                            if (node.data && node.data.invoice_id === idToExpand) {
                                                rowsToExpand.push(node);
                                            }
                                        });

                                        if (rowsToExpand.length > 0) {
                                            rowsToExpand[0].setExpanded(true);
                                        }
                                    }, 500);
                                }
                            },
                        };
                    },
                },
            ];
        },
        columnDefs() {
            return [
                {
                    field: 'quickbooks_id',
                    headerName: '',
                    hide: true,
                },
                {
                    field: 'modified',
                    headerName: 'Updated',
                    type: 'dateTimeColumn',
                    minWidth: 180,
                    maxWidth: 180,
                    sort: 'desc',
                },
                {
                    field: 'invoice_id',
                    headerName: 'Invoice #',
                    minWidth: 150,
                    maxWidth: 150,
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        innerRenderer: (params) => {
                            if (!params.node.rowPinned) {
                                return params.data.deleted
                                    ? params.data.invoice_id
                                    : `<a target='_blank' href='/app/reservation/edit/invoice/${
                                          params.data.invoice_id
                                      }'>${params.data.invoice_id}</a>`;
                            }
                            return '';
                        },
                    },
                },
                {
                    field: 'invoice.from_date',
                    headerName: 'From',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                    sortable: false,
                },
                {
                    field: 'invoice.to_date',
                    headerName: 'To',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                    sortable: false,
                },
                {
                    field: 'invoice.property_name',
                    headerName: 'Property',
                    minWidth: 200,
                    sortable: false,
                },
                {
                    field: 'customer.fullname',
                    headerName: 'Guest',
                    minWidth: 100,
                    sortable: false,
                },
                //{
                //    field: 'customer.guest.email',
                //    headerName: 'Guest Email',
                //    minWidth: 200,
                //    sortable: false,
                //},
                ...this.commonColumnDefs,
            ];
        },
        paymentColumnDefs() {
            return [
                {
                    field: 'modified',
                    headerName: 'Updated',
                    type: 'dateTimeColumn',
                    minWidth: 180,
                    maxWidth: 180,
                    sort: 'desc',
                },
                {
                    field: 'bill_id',
                    headerName: 'Payment ID',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'bill.date',
                    headerName: 'Date',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'bill.sum_paid',
                    headerName: 'Amount',
                    type: 'amountColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                ...this.commonColumnDefs,
            ];
        },
        detailCellRendererParams(masterGridParams) {
            return {
                detailGridOptions: {
                    columnDefs: this.paymentColumnDefs,
                    defaultColDef: {
                        sortable: false,
                        resizable: true,
                        suppressMovable: true,
                        suppressMenu: true,
                    },
                    suppressColumnSelectAll: true,
                    suppressMenu: true,
                    suppressContextMenu: true,
                    suppressCellFocus: true,
                    domLayout: 'autoHeight',
                    onGridReady: (params) => {
                        setTimeout(() => {
                            params.api.sizeColumnsToFit();
                        }, 100);
                    },
                },
                getDetailRowData: (params) => {
                    if (!params.data.payments || !Array.isArray(params.data.payments)) {
                        params.successCallback([]);
                        return;
                    }
                    params.successCallback(params.data.payments);
                },
            };
        },
        totalRecords() {
            if (this.$refs.reservations.totalRecords > 0) {
                return this.$refs.reservations.totalRecords;
            }
            return false;
        },
    },
    beforeMount() {
        this.statusBar = {
            statusPanels: [
                {
                    key: 'summaryBar',
                    statusPanel: 'summaryBar',
                    align: 'left',
                    statusPanelParams: {
                        totalRecords: () => this.totalRecords,
                    },
                },
            ],
        };
    },
    methods: {
        onGridReady(params) {
            this.detailCellRendererParams.detailGridOptions.columnTypes = this.$refs.reservations.columnTypes;
            this.$refs.reservations.gridOptions.getRowId = function(params) {
                return params.data.invoice_id;
            };
        },
        getObjectSufix(data) {
            if (!data) return '';
            if (data.type === 'RESERVATION') {
                return 'invoice?txnId=' + data.quickbooks_id;
            } else if (data.type === 'PAYMENT') {
                return 'recvpayment?txnId=' + data.quickbooks_id;
            }
            return '';
        },
        fetchReservations({ offset, limit, sort, desc, search }) {
            if (sort && sort.includes('last_error')) {
                sort = 'last_error';
            }
            return API.get(`${QUICKBOOKS_OBJECTS_ENDPOINT('reservation')}/grid/`, {
                params: {
                    offset,
                    limit,
                    sort,
                    desc,
                    search,
                    show_errors_only: this.showErrorsOnly,
                },
            });
        },
        isRowMaster(dataItem) {
            return dataItem && dataItem.payments && Array.isArray(dataItem.payments) && dataItem.payments.length > 0;
        },
        refreshReservations() {
            if (this.$refs.reservations) {
                this.$refs.reservations.gridOptions.api.resetColumnState();
                this.$refs.reservations.refreshDataSource();
            }
        },
    },
    watch: {
        shouldRefreshReservations() {
            this.refreshReservations();
        },
        showErrorsOnly() {
            this.refreshReservations();
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.objects-container {
    overflow: hidden;
    @include media-breakpoint-down(md) {
        overflow: scroll;
    }
}

:deep(.show-disabled-toggle) {
    width: 134px;
    @include media-breakpoint-down(lg) {
        width: 152px;
    }
}

:deep(.ag-details-row) {
    min-height: 50px;
    padding: 0 15px;
}

:deep(.ag-row) {
    cursor: pointer;
}

:deep(.ag-details-row .ag-details-grid) {
    margin: 5px 0;
    border: 1px solid #e5e5e5;
    border-radius: 4px;
}
</style>
